// Shared building blocks for every page: layout container, type styles,
// scroll reveals, chips and links. Keep page files about content, not styling.
import { forwardRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Flex, Heading, Link, Text } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { ACCENT, EASE, FG, GRID, MONO, MUTED, PANEL, SERIF, alpha } from "../theme";

export const MotionBox = motion(Box);
export const MotionFlex = motion(Flex);

export const PX = { base: 5, md: 10, xl: 16 };

export const Container = forwardRef(({ children, ...rest }, ref) => (
  <Box ref={ref} maxW="1360px" mx="auto" px={PX} {...rest}>
    {children}
  </Box>
));

// mono, uppercase, letter-spaced label — the "telemetry" voice of the site
export const Mono = ({ children, ...rest }) => (
  <Text
    fontFamily={MONO}
    fontSize="11px"
    letterSpacing="0.14em"
    textTransform="uppercase"
    color={MUTED}
    {...rest}
  >
    {children}
  </Text>
);

export const Eyebrow = ({ index, children, color = MUTED, ...rest }) => (
  <Flex align="center" gap={3} mb={4} {...rest}>
    {index && (
      <Mono color={ACCENT} letterSpacing="0.1em">
        {index}
      </Mono>
    )}
    <Box w="28px" h="1px" bg={GRID} />
    <Mono color={color}>{children}</Mono>
  </Flex>
);

// serif-italic accent for headline words
export const Em = ({ children, color = ACCENT, ...rest }) => (
  <Text
    as="span"
    fontFamily={SERIF}
    fontStyle="italic"
    fontWeight={400}
    letterSpacing="-0.01em"
    color={color}
    {...rest}
  >
    {children}
  </Text>
);

export const Display = ({ children, size = "xl", ...rest }) => {
  const sizes = {
    hero: { base: "52px", sm: "68px", md: "92px", lg: "112px", xl: "128px" },
    xl: { base: "44px", md: "68px", lg: "84px" },
    lg: { base: "36px", md: "52px", lg: "60px" },
    md: { base: "30px", md: "40px" },
    sm: { base: "24px", md: "30px" },
  };
  return (
    <Heading
      as="h1"
      fontSize={sizes[size]}
      lineHeight={size === "hero" ? 0.92 : 1}
      letterSpacing="-0.045em"
      fontWeight={600}
      color={FG}
      {...rest}
    >
      {children}
    </Heading>
  );
};

export const Lede = ({ children, ...rest }) => (
  <Text color="#c9c7bf" fontSize={{ base: "md", md: "lg" }} lineHeight="1.7" maxW="620px" {...rest}>
    {children}
  </Text>
);

// fade + rise when scrolled into view
export const Reveal = ({ children, delay = 0, y = 28, once = true, amount = 0.2, ...rest }) => (
  <MotionBox
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, amount }}
    transition={{ duration: 0.8, delay, ease: EASE }}
    {...rest}
  >
    {children}
  </MotionBox>
);

export const LiveDot = ({ color = ACCENT, size = "8px" }) => (
  <Box
    as="span"
    display="inline-block"
    w={size}
    h={size}
    borderRadius="full"
    bg={color}
    flexShrink={0}
    sx={{ animation: "pulse-dot 2s infinite" }}
  />
);

export const Chip = ({ children, color = MUTED, active, ...rest }) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    gap={1.5}
    px={2.5}
    py={1}
    borderRadius="full"
    border={`1px solid ${active ? color : GRID}`}
    bg={active ? alpha(color === MUTED ? FG : color, 0.1) : "transparent"}
    fontFamily={MONO}
    fontSize="10px"
    letterSpacing="0.1em"
    textTransform="uppercase"
    color={active ? color : MUTED}
    whiteSpace="nowrap"
    {...rest}
  >
    {children}
  </Box>
);

// text link with an arrow that nudges on hover; internal or external
export const ArrowLink = ({ to, href, children, color = ACCENT, ...rest }) => {
  const props = to
    ? { as: RouterLink, to }
    : { href, isExternal: true };
  return (
    <Link
      {...props}
      display="inline-flex"
      alignItems="center"
      gap={2}
      color={color}
      fontWeight={600}
      fontSize="sm"
      role="group"
      _hover={{ textDecoration: "none" }}
      {...rest}
    >
      <Box as="span" borderBottom="1px solid transparent" _groupHover={{ borderColor: color }}>
        {children}
      </Box>
      <Box as="span" transition="transform 0.3s" _groupHover={{ transform: "translateX(4px)" }}>
        {href && !to ? "↗" : "→"}
      </Box>
    </Link>
  );
};

export const SectionHead = ({ index, eyebrow, title, right, ...rest }) => (
  <Flex
    justify="space-between"
    align={{ base: "flex-start", md: "flex-end" }}
    direction={{ base: "column", md: "row" }}
    gap={4}
    mb={{ base: 8, md: 10 }}
    {...rest}
  >
    <Box>
      <Eyebrow index={index}>{eyebrow}</Eyebrow>
      <Display size="lg" as="h2">
        {title}
      </Display>
    </Box>
    {right}
  </Flex>
);

export const Card = ({ children, hover, ...rest }) => (
  <Box
    bg={PANEL}
    border={`1px solid ${GRID}`}
    borderRadius="2xl"
    transition="border-color 0.3s, transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)"
    _hover={hover ? { borderColor: "#3a3a41", transform: "translateY(-4px)" } : undefined}
    {...rest}
  >
    {children}
  </Box>
);

export const Loading = ({ label = "Loading", children }) => (
  <Flex minH="70vh" align="center" justify="center" direction="column" gap={4}>
    <Flex gap={2} align="center">
      <LiveDot />
      <Mono>{label}…</Mono>
    </Flex>
    {children}
  </Flex>
);

export const Hairline = (props) => <Box h="1px" bg={GRID} {...props} />;
